import type {
  EmailBlock,
  TextBlock,
  HeadingBlock,
  ImageBlock,
  ButtonBlock,
  DividerBlock,
  SpacerBlock,
  QuoteBlock,
} from './types';

// ─── Block → HTML ─────────────────────────────────────────────────────────────

/** Escapa texto que el usuario escribió, para que sea CONTENIDO y no marcado.
 *
 *  Antes se interpolaba crudo: un título «Becas & Ayudas» emitía un `&` suelto,
 *  y «Nota: precio < 100» se comía el resto de la línea en varios clientes de
 *  correo. No es una preocupación de seguridad —quien redacta ya puede mandar
 *  HTML a propósito— sino de que el texto salga como se escribió. */
function esc(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Texto plano del usuario → HTML, respetando los saltos de línea. */
function escMultiline(value: string): string {
  return esc(value).replace(/\n/g, '<br/>');
}

/** EL BLOQUE DEL MODO «HTML AVANZADO» ES LA EXCEPCIÓN: su contenido ES marcado
 *  y se emite tal cual. Se reconoce por su id, que es el contrato que fija
 *  `handleRawHtmlChange` al colapsar los bloques. */
const RAW_BLOCK_ID = 'raw';

function blockToHtml(block: EmailBlock): string {
  switch (block.type) {
    case 'text': {
      const b = block as TextBlock;
      const cuerpo = b.id === RAW_BLOCK_ID ? b.content : escMultiline(b.content);
      return `<p style="margin:0 0 16px;font-size:15px;line-height:1.7;color:#444;text-align:${b.align ?? 'left'};">${cuerpo}</p>`;
    }
    case 'heading': {
      const b = block as HeadingBlock;
      return `<h2 style="margin:0 0 16px;font-size:20px;font-weight:700;color:${esc(b.color)};">${esc(b.content)}</h2>`;
    }
    case 'image': {
      const b = block as ImageBlock;
      return `<p style="text-align:center;margin:0 0 16px;"><img src="${esc(b.url)}" alt="${esc(b.alt)}" style="width:${b.width}%;max-width:100%;border-radius:4px;" /></p>`;
    }
    case 'button': {
      const b = block as ButtonBlock;
      return `<p style="text-align:center;margin:24px 0;">
  <a href="${esc(b.url)}" style="display:inline-block;background:${esc(b.color)};color:#ffffff;text-decoration:none;font-size:15px;font-weight:600;padding:14px 36px;border-radius:6px;">${esc(b.label)}</a>
</p>`;
    }
    case 'divider': {
      return `<hr style="border:none;border-top:1px solid #dde3ed;margin:24px 0;" />`;
    }
    case 'spacer': {
      const b = block as SpacerBlock;
      return `<div style="height:${b.height}px;"></div>`;
    }
    case 'quote': {
      const b = block as QuoteBlock;
      return `<table width="100%" cellpadding="0" cellspacing="0" style="margin:0 0 16px;">
  <tr>
    <td style="background:#f8f9fc;border-radius:4px;border-left:3px solid ${esc(b.accentColor)};padding:14px 18px;">
      <p style="margin:0;font-size:15px;color:#555;line-height:1.6;font-style:italic;">${escMultiline(b.content)}</p>
    </td>
  </tr>
</table>`;
    }
    default:
      return '';
  }
}

export function blocksToHtml(blocks: EmailBlock[]): string {
  return blocks.map(blockToHtml).join('\n');
}

// ─── Preview HTML builder (mirrors backend buildEmailWrapper) ─────────────────

export function buildPreviewHtml(opts: {
  tenantName: string;
  primaryColor: string;
  logoUrl?: string;
  bodyHtml: string;
  subject: string;
}): string {
  const { tenantName, primaryColor, logoUrl, bodyHtml, subject } = opts;
  const logoBlock = logoUrl
    ? `<img src="${logoUrl}" alt="${tenantName}"
         style="display:block;margin:0 auto 12px;max-height:56px;max-width:180px;object-fit:contain;" />`
    : '';

  // Replace merge fields with preview placeholders
  const preview = bodyHtml
    .replace(/\{\{nombre\}\}/g, 'Estimado Contacto')
    .replace(/\{\{nombre_primero\}\}/g, 'Amigo')
    .replace(/\{\{correo\}\}/g, 'ejemplo@correo.com');

  return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8" />
  <title>${subject || 'Vista Previa'}</title>
</head>
<body style="margin:0;padding:0;background:#f4f6f9;font-family:'Segoe UI',Helvetica,Arial,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background:#f4f6f9;padding:40px 16px;">
    <tr><td align="center">
      <table width="560" cellpadding="0" cellspacing="0"
             style="background:#fff;border-radius:8px;border:1px solid #dde3ed;overflow:hidden;max-width:100%;">
        <tr>
          <td style="background:${primaryColor};padding:32px 40px 24px;text-align:center;">
            ${logoBlock}
            <p style="margin:0 0 4px;font-size:11px;font-weight:600;letter-spacing:2px;color:#fff;opacity:0.65;text-transform:uppercase;">Seminario Teológico</p>
            <h1 style="margin:0;font-size:22px;font-weight:700;color:#fff;">${tenantName}</h1>
          </td>
        </tr>
        <tr>
          <td style="padding:40px 40px 32px;">${preview}</td>
        </tr>
        <tr>
          <td style="background:#f4f6f9;padding:18px 40px;border-top:1px solid #dde3ed;text-align:center;">
            <p style="margin:0;font-size:12px;color:#888;">${tenantName} &bull; Portal del Alumno</p>
            <p style="margin:8px 0 0;font-size:11px;color:#aaa;">Si no deseas recibir más comunicaciones, <a href="#preview" style="color:#aaa;">cancela tu suscripción</a>.</p>
          </td>
        </tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;
}

// ─── Default block factories ──────────────────────────────────────────────────

function uid() {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);
}

export function createDefaultBlock(type: EmailBlock['type'], primaryColor: string): EmailBlock {
  switch (type) {
    case 'text':
      return {
        id: uid(),
        type: 'text',
        content: 'Escribe tu texto aquí...',
        align: 'left',
      } satisfies TextBlock;
    case 'heading':
      return {
        id: uid(),
        type: 'heading',
        content: 'Título de Sección',
        color: primaryColor,
      } satisfies HeadingBlock;
    case 'image':
      return { id: uid(), type: 'image', url: '', alt: 'Imagen', width: 100 } satisfies ImageBlock;
    case 'button':
      return {
        id: uid(),
        type: 'button',
        label: 'Ver más detalles',
        url: 'https://',
        color: primaryColor,
      } satisfies ButtonBlock;
    case 'divider':
      return { id: uid(), type: 'divider' } satisfies DividerBlock;
    case 'spacer':
      return { id: uid(), type: 'spacer', height: 24 } satisfies SpacerBlock;
    case 'quote':
      return {
        id: uid(),
        type: 'quote',
        content: 'Texto de cita o destacado...',
        accentColor: primaryColor,
      } satisfies QuoteBlock;
  }
}

// ─── Template → blocks ───────────────────────────────────────────────────────

/** Converts a legacy HTML template into a single text block for backward compat */
export function htmlToBlocks(html: string): EmailBlock[] {
  if (!html.trim()) return [];
  // Wrap raw HTML as a single text block so old templates work immediately
  return [{ id: uid(), type: 'text', content: html, align: 'left' } satisfies TextBlock];
}

// ─── Template catalog ─────────────────────────────────────────────────────────

export function buildStarterBlocks(
  templateId: string,
  tenantName: string,
  primaryColor: string,
): EmailBlock[] {
  switch (templateId) {
    case 'newsletter':
      return [
        {
          id: uid(),
          type: 'text',
          content: `Hola, {{nombre_primero}},`,
          align: 'left',
        } satisfies TextBlock,
        {
          id: uid(),
          type: 'text',
          content: `Te compartimos las últimas novedades de nuestra comunidad académica.`,
          align: 'left',
        } satisfies TextBlock,
        {
          id: uid(),
          type: 'heading',
          content: 'Novedades del Seminario',
          color: primaryColor,
        } satisfies HeadingBlock,
        {
          id: uid(),
          type: 'text',
          content: `• Nuevo Curso: [Nombre del curso]\n• Próximo Evento: [Fecha y Detalles]`,
          align: 'left',
        } satisfies TextBlock,
        {
          id: uid(),
          type: 'button',
          label: 'Ver más detalles',
          url: 'https://',
          color: primaryColor,
        } satisfies ButtonBlock,
        { id: uid(), type: 'divider' } satisfies DividerBlock,
        {
          id: uid(),
          type: 'text',
          content: `Bendiciones,\n${tenantName}`,
          align: 'left',
        } satisfies TextBlock,
      ];
    case 'alert':
      return [
        {
          id: uid(),
          type: 'heading',
          content: 'Aviso Institucional',
          color: primaryColor,
        } satisfies HeadingBlock,
        {
          id: uid(),
          type: 'text',
          content: `Estimado/a {{nombre}},`,
          align: 'left',
        } satisfies TextBlock,
        {
          id: uid(),
          type: 'quote',
          content: 'Por favor inserta los detalles del aviso aquí.',
          accentColor: '#f59e0b',
        } satisfies QuoteBlock,
        {
          id: uid(),
          type: 'text',
          content: `Atentamente,\nAdministración de ${tenantName}`,
          align: 'left',
        } satisfies TextBlock,
      ];
    default: // blank
      return [
        {
          id: uid(),
          type: 'text',
          content: `Hola, {{nombre_primero}},\n\nEscribe aquí el contenido de tu campaña...`,
          align: 'left',
        } satisfies TextBlock,
      ];
  }
}

// ─── Audience labels (no emojis) ─────────────────────────────────────────────

export const AUDIENCE_OPTIONS_LIST = [
  { value: 'all_crm', label: 'Todos los contactos CRM' },
  { value: 'tag', label: 'Por etiqueta CRM' },
  { value: 'by_person_type', label: 'Por tipo de persona' },
  { value: 'by_institutional_role', label: 'Por cargo institucional' },
  { value: 'by_program', label: 'Por programa de estudio' },
  { value: 'by_course', label: 'Por materia' },
  { value: 'custom_contacts', label: 'Selección personalizada' },
] as const;
